import { Autocomplete, Box, capitalize, Checkbox, createTheme, FormControlLabel, Menu, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, ThemeProvider, Typography } from "@mui/material"
import { useFormik } from "formik"
import React, { useMemo } from "react"
import { useSelector } from "react-redux"
import Spinner from "../../components/Spinner"
import toastWrapper from "../../components/toastWrapper"
import useMenu from "../../hooks/useMenu"
import { useEmployeeGroupSubjects } from "../../redux/employeeGroupSubjectsSlice"
import { useFinalMarkLogBook } from "../../redux/finalMarkLogBookSlice"
import { useLogBook } from "../../redux/logBookSlice"
import theme from "../../theme"
import { getAllDaysInMonth } from "../../utils"
import FinalMarkField from "./components/FinalMarkField"
import MarkField from "./components/MarkField"

const tableTheme = createTheme({
    ...theme,
    components: {
        ...theme.components,
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderRight: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "#ccc",
                    margin: 0,
                    padding: 0,
                    minWidth: 20,
                    "&:nth-of-type(1)": {
                        position: "sticky",
                        left: 0,
                        background: "white",
                        borderRight: "1px solid",
                        borderLeft: "1px solid",
                        borderBottom: "1px solid",
                        borderColor: "#ccc",
                    }
                }
            },
            defaultProps: {
                align: "center"
            }
        },
        MuiTableRow: { styleOverrides: { root: { border: "0" } } }
    }
})

const getISOdate = date => {
    let m = date.getMonth() + 1
    let d = date.getDate()
    if (`${m}`.length === 1) { m = `0${m}` }
    if (`${d}`.length === 1) { d = `0${d}` }
    return `${date.getFullYear()}-${m}-${d}`
}

const GroupSubjectLogBook = () => {

    const userInfo = useSelector(x => x.user.userInfo)

    const { groupSubjects, groupSubjectsIsLoading } = useEmployeeGroupSubjects(userInfo?.role?.id)

    const studyYearOptions = Array.from({ length: new Date().getFullYear() + 1 - 2023 }, (_, i) => i + 2023).reverse()

    const { values, setFieldValue } = useFormik({
        initialValues: {
            studyYear: studyYearOptions[0],
            groupSubject: null,
            showDaysWithLessons: true,
            monthStart: null,
            monthEnd: null,
        }
    })

    React.useEffect(() => {
        if (!values.groupSubject && groupSubjects.length > 0) {
            setFieldValue("groupSubject", groupSubjects[0])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupSubjects])

    const { logBook, logBookIsLoading, updateLogbook } = useLogBook(
        values?.groupSubject?.group_id,
        values?.groupSubject?.subject_id,
        values.studyYear
    )

    const lessonDays = React.useMemo(() => {
        const days = []
        logBook.forEach(student => { days.push(...Object.keys(student.logbook_items)) })
        const uniqueDays = Array.from(new Set(days)).toSorted()
        return uniqueDays?.map(d => new Date(d)) ?? []
    }, [logBook])

    const months = useMemo(() => {
        const defaultMonths = [
            { index: 8, year: values.studyYear, days: getAllDaysInMonth(8, values.studyYear) },
            { index: 9, year: values.studyYear, days: getAllDaysInMonth(9, values.studyYear) },
            { index: 10, year: values.studyYear, days: getAllDaysInMonth(10, values.studyYear) },
            { index: 11, year: values.studyYear, days: getAllDaysInMonth(11, values.studyYear) },
            { index: 0, year: values.studyYear + 1, days: getAllDaysInMonth(0, values.studyYear + 1) },
            { index: 1, year: values.studyYear + 1, days: getAllDaysInMonth(1, values.studyYear + 1) },
            { index: 2, year: values.studyYear + 1, days: getAllDaysInMonth(2, values.studyYear + 1) },
            { index: 3, year: values.studyYear + 1, days: getAllDaysInMonth(3, values.studyYear + 1) },
            { index: 4, year: values.studyYear + 1, days: getAllDaysInMonth(4, values.studyYear + 1) },
            { index: 5, year: values.studyYear + 1, days: getAllDaysInMonth(5, values.studyYear + 1) },
            { index: 6, year: values.studyYear + 1, days: getAllDaysInMonth(6, values.studyYear + 1) },
            { index: 7, year: values.studyYear + 1, days: getAllDaysInMonth(7, values.studyYear + 1) },
        ]

        if (!values.showDaysWithLessons) {
            return defaultMonths
        } else {
            return defaultMonths
                .filter(month => lessonDays.find(ld => ld.getMonth() === month.index))
                .map(month => ({ ...month, days: lessonDays.filter(d => d.getMonth() === month.index) }))
        }
    }, [values.studyYear, values.showDaysWithLessons, lessonDays])

    const {
        finalMarkLogBook, finalMarkLogBookIsLoading, finalMarkLogBookIsUpdating,
        handleCreateFinalMark, handleUpdateFinalMark
    } = useFinalMarkLogBook(
        values?.groupSubject?.group_id,
        values?.groupSubject?.subject_id,
        values.studyYear
    )

    const [tabValue, setTabValue] = React.useState(0)

    const { anchorEl, open, handleDefaultMenuClick, handleMenuClose } = useMenu()
    const [editingMarkbook, setEditingMarkbook] = React.useState(null)

    const handleOpenMarkbookMenu = (event, data, lessonIndex, studentIndex) => {
        handleDefaultMenuClick(event)
        setEditingMarkbook({ ...data, lessonIndex, studentIndex })
    }

    const handleCloseMarkbookMenu = () => {
        setEditingMarkbook(null)
        handleMenuClose()
    }

    const handleUpdateLogbook = value => {
        handleMenuClose()
        updateLogbook(editingMarkbook, value)
            .then(() => { setEditingMarkbook(null) })
            .catch(e => {
                setEditingMarkbook(null)
                toastWrapper("Не удалось поставить оценку", "error")
                console.error(e)
            })
    }

    return <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", p: 2 }}>

        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "column", md: "row" } }}>

            <Autocomplete
                options={studyYearOptions}
                value={values.studyYear}
                onChange={(_, value) => setFieldValue("studyYear", value)}
                disableClearable
                getOptionLabel={option => `${option} - ${option + 1} уч. год`}
                renderInput={props => <TextField
                    label="Учебный год"
                    sx={{ minWidth: { md: 250, sm: 300, xs: "100%" }, maxWidth: { md: 250, sm: 300, xs: "100%" } }}
                    {...props}
                />}
            />

            <Autocomplete
                options={groupSubjects}
                value={values.groupSubject}
                onChange={(_, value) => setFieldValue("groupSubject", value)}
                getOptionLabel={option => `${option.subject_name} - ${option.group_name}`}
                disableClearable
                disabled={groupSubjectsIsLoading}
                loading={true}
                loadingText="Загрузка"
                renderInput={props => <TextField
                    label="Курс"
                    sx={{ minWidth: { md: 250, sm: 300, xs: "100%" }, maxWidth: { md: 250, sm: 300, xs: "100%" } }}
                    {...props}
                />}
            />

            <FormControlLabel
                label="Только дни с занятиями"
                sx={{ minWidth: { md: 250, sm: 300, xs: "100%" }, maxWidth: { md: 250, sm: 300, xs: "100%" } }}
                control={<Checkbox
                    checked={values.showDaysWithLessons}
                    onChange={(_, value) => setFieldValue("showDaysWithLessons", value)}
                    size="small"
                />}
            />

        </Box>

        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ mb: 1 }}>
            <Tab label="Журнал" value={0} />
            <Tab label="Итоговые оценки" value={1} />
        </Tabs>


        <ThemeProvider theme={tableTheme}>

            {tabValue === 0 && <>
                {logBookIsLoading ? <Spinner height="200px" /> :
                    <TableContainer sx={{ mt: 2 }}>
                        <Table
                            stickyHeader
                            sx={{ overflowX: "scroll", minHeight: 200, borderTop: 1, borderColor: "#ccc", borderCollapse: "separate" }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ maxWidth: 200, }} />
                                    {months.map(month => <TableCell
                                        key={month.index}
                                        align="center"
                                        colSpan={month.days.length}
                                    >
                                        {capitalize(new Date(1970, month.index, 1).toLocaleDateString('ru-RU', { month: "long" }) ?? "")}
                                    </TableCell>)}
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ maxWidth: 200, }} />
                                    {months.map(month => month.days.map(day => <TableCell
                                        key={`${month.index}-${day.getDate()}`}
                                        sx={{ bgcolor: day.getDay() === 0 ? "#ddd" : "inherit" }}
                                    >
                                        {day.getDate()}
                                    </TableCell>))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logBook.map((student, studentIndex) => <TableRow key={student.id}>
                                    <TableCell sx={{ p: 0.5, maxWidth: 200 }}>
                                        <Typography noWrap variant="body2">
                                            {student.student}
                                        </Typography>
                                    </TableCell>
                                    {months.map(month => month.days.map(day => <TableCell
                                        key={`${month.index}-${day.getDate()}-${student.id}`}
                                        sx={{ bgcolor: day.getDay() === 0 ? "#ddd" : "inherit", minWidth: 25 }}
                                    >
                                        <Stack direction="row">
                                            {student.logbook_items[getISOdate(day)]?.map((lesson, lessonIndex) => <MarkField
                                                lessonIndex={lessonIndex}
                                                studentIndex={studentIndex}
                                                key={`${month.index}-${day.getDate()}-${student.id}-${lesson.lesson}`}
                                                onClick={handleOpenMarkbookMenu}
                                                data={lesson}
                                            />)}
                                        </Stack>
                                    </TableCell>))}
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </TableContainer>}
            </>}

            {tabValue === 1 && <>
                {finalMarkLogBookIsLoading ? <Spinner height="200px" /> :
                    <TableContainer sx={{ mt: 2 }}>
                        <Table sx={{
                            maxWidth: 200,
                            overflowX: "scroll",
                            minHeight: 200,
                            borderTop: 1,
                            borderColor: "#ccc",
                            borderCollapse: "separate"
                        }}>
                            <TableBody>
                                {finalMarkLogBook.map(student => <TableRow key={student.id}>
                                    <TableCell sx={{ p: 0.5 }}>
                                        <Typography noWrap variant="body2" align="left">
                                            {student.student}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 20 }}>
                                        <FinalMarkField
                                            value={student.final_mark?.mark}
                                            onChange={mark => {
                                                if (student.final_mark?.id) {
                                                    handleUpdateFinalMark({ ...student.final_mark, mark })
                                                } else {
                                                    handleCreateFinalMark({
                                                        mark, study_year: values.studyYear,
                                                        subject: values.groupSubject.subject_id,
                                                        student: student.id,
                                                    })
                                                }
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </TableContainer>}
            </>}
        </ThemeProvider>
        <Menu open={open} onClose={handleCloseMarkbookMenu} anchorEl={anchorEl}>
            <MenuItem onClick={() => handleUpdateLogbook(5)}>5</MenuItem>
            <MenuItem onClick={() => handleUpdateLogbook(4)}>4</MenuItem>
            <MenuItem onClick={() => handleUpdateLogbook(3)}>3</MenuItem>
            <MenuItem onClick={() => handleUpdateLogbook(2)}>2</MenuItem>
            <MenuItem onClick={() => handleUpdateLogbook("Н")}>Н</MenuItem>
            <MenuItem onClick={() => handleUpdateLogbook("К")}>К</MenuItem>
            <MenuItem onClick={() => handleUpdateLogbook("·")}>·</MenuItem>
        </Menu>
    </Box>
}

export default GroupSubjectLogBook
